import * as React from 'react';
import { createPortal } from 'react-dom';

const RIGHT_PORTAL_ID = 'cp3-header-actions';
const LEFT_PORTAL_ID = 'cp3-header-actions-left';

/**
 * Teleports its children into the SiteHeader's right-hand slot. Pages
 * use this to surface their primary actions (search, "new …", sync,
 * etc.) directly in the top bar instead of showing a duplicated header
 * inside the page body.
 *
 * The portal target is rendered once by `<AppShell/>` as a `<div id="…">`.
 * If the target isn't mounted yet (initial paint), we re-poll on the
 * next frame; that handles the order-of-mount race without flicker.
 */
export function HeaderActions({ children }: { children: React.ReactNode }) {
  return <PortalInto id={RIGHT_PORTAL_ID}>{children}</PortalInto>;
}

/**
 * Left-side counterpart — teleports children into the slot directly
 * after the page title. Used by pages whose primary controls (e.g.
 * date nav, period label) belong next to the heading instead of being
 * pushed all the way to the right of the bar.
 */
export function HeaderActionsLeft({ children }: { children: React.ReactNode }) {
  return <PortalInto id={LEFT_PORTAL_ID}>{children}</PortalInto>;
}

function PortalInto({ id, children }: { id: string; children: React.ReactNode }) {
  const [target, setTarget] = React.useState<HTMLElement | null>(() =>
    typeof document !== 'undefined' ? document.getElementById(id) : null,
  );
  React.useEffect(() => {
    if (target) return;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) setTarget(el);
      else requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [target, id]);
  if (!target) return null;
  return createPortal(children, target);
}

/** Right-side placeholder (push-to-end via `ml-auto`). */
export function HeaderActionsSlot() {
  return <div id={RIGHT_PORTAL_ID} className="ml-auto flex items-center gap-2" />;
}

/** Left-side placeholder rendered immediately after the page title. */
export function HeaderActionsLeftSlot() {
  return <div id={LEFT_PORTAL_ID} className="flex items-center gap-1.5" />;
}
